/**
 * useOnlineStatus — detect online / offline state for the app shell.
 *
 * Source of truth ordering:
 *   1. `navigator.onLine` — fast, but unreliable on captive-portal networks.
 *   2. Background ping to `/api/v1/core/ping/` every 60 s while the page is
 *      visible — corrects captive-portal "zombie online" states.
 *
 * The hook returns a snapshot with the most recent transition time and the
 * source that produced the current value. Components should treat any change
 * to `isOnline` as a transition signal (e.g. trigger the sync worker).
 */
import { useEffect, useRef, useState } from "react";

export type OnlineStatusSource = "navigator" | "ping";

export interface OnlineStatus {
  isOnline: boolean;
  lastChangedAt: Date;
  source: OnlineStatusSource;
}

const PING_INTERVAL_MS = 60_000;
const PING_TIMEOUT_MS = 8_000;
const PING_URL = "/api/v1/core/ping/";

function readNavigatorOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  // navigator.onLine is "false" only when there is definitely no network.
  // It is not authoritative when it returns "true".
  return navigator.onLine !== false;
}

async function pingServer(signal: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(PING_URL, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal,
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function useOnlineStatus(): OnlineStatus {
  const [status, setStatus] = useState<OnlineStatus>(() => ({
    isOnline: readNavigatorOnline(),
    lastChangedAt: new Date(),
    source: "navigator",
  }));
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (typeof window === "undefined") return;

    function transition(next: boolean, source: OnlineStatusSource) {
      const current = statusRef.current;
      if (current.isOnline === next && current.source === source) return;
      if (current.isOnline === next) return;
      setStatus({ isOnline: next, lastChangedAt: new Date(), source });
    }

    function handleOnline() {
      transition(true, "navigator");
    }
    function handleOffline() {
      transition(false, "navigator");
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let inflight: AbortController | null = null;

    async function runPing() {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      inflight?.abort();
      const ctrl = new AbortController();
      inflight = ctrl;
      const timeoutId = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
      try {
        const reachable = await pingServer(ctrl.signal);
        transition(reachable, "ping");
      } finally {
        clearTimeout(timeoutId);
        if (inflight === ctrl) inflight = null;
      }
    }

    function startPing() {
      if (pingTimer) return;
      pingTimer = setInterval(runPing, PING_INTERVAL_MS);
    }

    function stopPing() {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      inflight?.abort();
      inflight = null;
    }

    function handleVisibility() {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        startPing();
      } else {
        stopPing();
      }
    }

    handleVisibility();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
      stopPing();
    };
  }, []);

  return status;
}
